<template>
  <div class="ncrm-mailjet-activity">
    <!-- Header: title + refresh -->
    <div class="ncrm-mailjet-activity__header">
      <div class="d-flex align-center ga-2">
        <v-icon size="20" color="primary">mdi-email-fast-outline</v-icon>
        <span class="text-subtitle-2 ncrm-text-strong">
          {{ $t("ncrm.integrations.mailjet.activity.title") }}
        </span>
        <span v-if="data?.entity_label" class="text-caption text-medium-emphasis">
          • {{ data.entity_label }}
        </span>
      </div>
      <v-btn
        variant="text"
        size="small"
        :loading="loading"
        :disabled="!entityId"
        @click="fetchActivity"
      >
        <v-icon start size="16">mdi-refresh</v-icon>
        {{ $t("ncrm.integrations.mailjet.activity.refresh") }}
      </v-btn>
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading && !data" class="ncrm-mailjet-activity__loader">
      <v-skeleton-loader type="card" :loading="true" />
    </div>

    <!-- Error state -->
    <v-alert v-else-if="errorMessage" type="error" density="compact">
      {{ errorMessage }}
    </v-alert>

    <!-- Empty state -->
    <v-alert
      v-else-if="data && data.contacts.length === 0"
      type="info"
      density="comfortable"
    >
      <div class="text-body-2">
        {{ $t("ncrm.integrations.mailjet.activity.empty.message") }}
      </div>
      <div class="text-caption text-medium-emphasis mt-1 mb-2">
        {{ $t("ncrm.integrations.mailjet.activity.empty.howToLink") }}
      </div>
      <div v-if="canWriteMailjet" class="mt-2">
        <v-btn
          color="primary"
          variant="flat"
          size="small"
          @click="openAddContact"
        >
          <v-icon start size="16">mdi-account-plus-outline</v-icon>
          {{ $t("ncrm.integrations.mailjet.activity.addContact") }}
        </v-btn>
      </div>
    </v-alert>

    <!-- Main content: 1+ contacts -->
    <template v-else-if="data && data.contacts.length > 0">
      <!-- Fallback banner: lead/deal/contract used company aggregate -->
      <v-alert
        v-if="data.fallback_used"
        type="info"
        density="compact"
        class="mb-3"
      >
        {{ $t("ncrm.integrations.mailjet.activity.fallbackUsedBanner") }}
      </v-alert>

      <!-- Aggregate header (only for company / fallback) -->
      <div v-if="isAggregate" class="ncrm-mailjet-activity__aggregate-header mb-3">
        <v-chip color="primary">
          <v-icon start size="14">mdi-account-multiple-outline</v-icon>
          {{ $t("ncrm.integrations.mailjet.activity.multiContactsTitle", { count: data.contacts.length }) }}
        </v-chip>
      </div>

      <!-- Per-contact card -->
      <v-card
        v-for="contact in data.contacts"
        :key="contact.id"
        variant="outlined"
        class="ncrm-mailjet-activity__contact-card mb-3"
      >
        <v-card-text>
          <!-- Contact identity row -->
          <div class="ncrm-mailjet-activity__identity mb-3">
            <div>
              <div class="text-body-2 ncrm-text-strong">{{ contact.email }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t("ncrm.integrations.mailjet.activity.account") }}: {{ contact.account_name }}
                <span v-if="contact.last_synced_at" class="ml-2">
                  • {{ $t("ncrm.integrations.mailjet.activity.lastSynced") }}: {{ relativeTime(contact.last_synced_at) }}
                </span>
              </div>
            </div>
            <v-spacer />
            <a
              v-if="contact.linked_person && contact.linked_person.id"
              class="text-caption text-primary"
              :href="`/ncrm/people/${contact.linked_person.id}`"
              @click.prevent="goToPerson(contact.linked_person.id)"
            >
              {{ contact.linked_person.name || contact.email }}
              <v-icon size="12">mdi-arrow-right</v-icon>
            </a>
          </div>

          <!-- Excluded warning -->
          <v-alert
            v-if="contact.is_excluded_from_campaigns"
            type="warning"
            density="compact"
            class="mb-3"
          >
            {{ $t("ncrm.integrations.mailjet.activity.excluded") }}
          </v-alert>

          <!-- Stats 30 days -->
          <div class="ncrm-mailjet-activity__section-label">
            {{ $t("ncrm.integrations.mailjet.activity.stats30Days.title") }}
          </div>
          <div class="ncrm-mailjet-activity__stats">
            <div
              v-for="key in statKeys"
              :key="key"
              class="ncrm-mailjet-activity__stat-tile"
              :class="`ncrm-mailjet-activity__stat-tile--${statColor(key)}`"
            >
              <div class="ncrm-mailjet-activity__stat-value">
                {{ contact.stats_30d[key] || 0 }}
              </div>
              <div class="ncrm-mailjet-activity__stat-label">
                {{ $t(`ncrm.integrations.mailjet.activity.stats30Days.${key}`) }}
              </div>
            </div>
          </div>

          <!-- Action buttons row -->
          <div v-if="canWriteMailjet" class="d-flex align-center ga-2 mt-4 mb-3">
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              @click="openSendEmail(contact)"
            >
              <v-icon start size="16">mdi-email-outline</v-icon>
              {{ $t("ncrm.integrations.mailjet.activity.sendEmail") }}
            </v-btn>
            <v-btn
              size="small"
              variant="tonal"
              @click="openManageLists(contact)"
            >
              <v-icon start size="16">mdi-format-list-checks</v-icon>
              {{ $t("ncrm.integrations.mailjet.activity.lists.manage") }}
            </v-btn>
          </div>

          <!-- Lists -->
          <div class="d-flex align-center justify-space-between">
            <div class="ncrm-mailjet-activity__section-label" style="margin-bottom: 0">
              {{ $t("ncrm.integrations.mailjet.activity.lists.title", { count: contact.memberships.length }) }}
            </div>
          </div>
          <div v-if="contact.memberships.length" class="ncrm-mailjet-activity__lists mt-2">
            <v-chip
              v-for="m in contact.memberships"
              :key="m.list_id"
              :color="m.is_unsubscribed ? 'error' : 'success'"
            >
              <v-icon start size="13">{{ m.is_unsubscribed ? "mdi-close-circle" : "mdi-check-circle" }}</v-icon>
              {{ m.list_name }}
              <span v-if="m.is_unsubscribed" class="ml-1 text-caption">
                ({{ $t("ncrm.integrations.mailjet.activity.lists.unsubscribed") }})
              </span>
            </v-chip>
          </div>
          <div v-else class="text-caption text-medium-emphasis mt-2">
            {{ $t("ncrm.integrations.mailjet.activity.lists.none") }}
          </div>

          <!-- Recent events -->
          <template v-if="contact.recent_events.length">
            <div class="ncrm-mailjet-activity__section-label mt-4">
              {{ $t("ncrm.integrations.mailjet.activity.recentActivity.title") }}
            </div>
            <div class="ncrm-mailjet-activity__events">
              <div
                v-for="event in contact.recent_events"
                :key="event.id"
                class="ncrm-mailjet-activity__event-row"
              >
                <v-chip
                  :color="eventColor(event.event_type)"
                  size="x-small"
                  class="ncrm-mailjet-activity__event-chip"
                >
                  {{ event.event_type }}
                </v-chip>
                <span class="ncrm-mailjet-activity__event-subject">
                  {{ event.campaign_subject || $t("ncrm.integrations.mailjet.activity.noCampaign") }}
                </span>
                <span class="ncrm-mailjet-activity__event-time text-caption text-medium-emphasis">
                  {{ relativeTime(event.event_at) }}
                </span>
              </div>
            </div>
          </template>
        </v-card-text>
      </v-card>
    </template>

    <!-- Manage-lists modal: shared across all contacts in this panel -->
    <NcrmMailjetManageListsDialog
      v-if="manageDialog.contact"
      v-model="manageDialog.open"
      :account-id="manageDialog.contact.account_id"
      :contact-id="manageDialog.contact.id"
      :contact-email="manageDialog.contact.email"
      :initial-list-ids="manageDialog.activeListIds"
      @saved="onListsSaved"
      @notify="$emit('notify', $event)"
    />

    <!-- Send-email modal: pick template + preview + send -->
    <NcrmMailjetSendEmailDialog
      v-if="sendDialog.contact"
      v-model="sendDialog.open"
      :account-id="sendDialog.contact.account_id"
      :contact-id="sendDialog.contact.id"
      :contact-email="sendDialog.contact.email"
      :sender-email="sendDialog.senderEmail"
      :sender-name="sendDialog.senderName"
      @sent="onEmailSent"
      @notify="$emit('notify', $event)"
    />

    <!-- Other entity emails not yet linked to a Mailjet contact. Extracted
         to its own component to keep this file under the size policy. Only
         renders when canWrite + at least one contact already exists (the
         empty-state already exposes the same CTA via its own button). -->
    <NcrmMailjetUnlinkedEmailsList
      v-if="canWriteMailjet && data && data.contacts.length > 0"
      :emails="unlinkedEmails"
      @add="openAddContact"
    />

    <NcrmMailjetAddContactDialog
      v-if="addContactDialog.open"
      v-model="addContactDialog.open"
      :account-id="addContactDialog.accountId"
      :accounts="data?.available_accounts || []"
      :entity-type="entityType"
      :entity-id="entityId"
      :preset-email="addContactDialog.presetEmail"
      @saved="onContactAdded"
      @notify="$emit('notify', $event)"
    />
  </div>
</template>

<script>
import axios from "axios";
import NcrmMailjetManageListsDialog from "./NcrmMailjetManageListsDialog.vue";
import NcrmMailjetSendEmailDialog from "./NcrmMailjetSendEmailDialog.vue";
import NcrmMailjetAddContactDialog from "./NcrmMailjetAddContactDialog.vue";
import NcrmMailjetUnlinkedEmailsList from "./NcrmMailjetUnlinkedEmailsList.vue";
import { formatDisplayDate, formatDisplayDateTime } from "@/Utile/displayFormat.mjs";

// Same color hints used in Events tab — keeps the visual semantics
// consistent between the workspace tab and this in-page panel.
const EVENT_COLORS = {
  sent: "info",
  open: "success",
  click: "success",
  unsub: "warning",
  spam: "error",
  blocked: "error",
  hardbounce: "error",
  softbounce: "warning",
  bounce: "warning",
};

// Stats tiles we always render (matches STATS_KEYS on the backend). Order
// here is the display order, deliberately ranked by user value: most
// engagement first, then negative signals at the end.
const STAT_KEYS = ["sent", "open", "click", "bounce", "unsub"];

export default {
  name: "NcrmMailjetActivityPanel",
  components: {
    NcrmMailjetManageListsDialog,
    NcrmMailjetSendEmailDialog,
    NcrmMailjetAddContactDialog,
    NcrmMailjetUnlinkedEmailsList,
  },
  props: {
    entityType: {
      type: String,
      required: true,
      validator: (v) => ["person", "company", "lead", "deal", "contract"].includes(v),
    },
    entityId: {
      type: [Number, String],
      required: true,
    },
  },
  emits: ["notify"],
  data() {
    return {
      data: null,
      loading: false,
      errorMessage: "",
      statKeys: STAT_KEYS,
      // Shared state for the manage-lists modal — only one dialog open
      // at a time, and we pass the currently-selected contact + its active
      // memberships through to the dialog as props.
      manageDialog: {
        open: false,
        contact: null,
        activeListIds: [],
      },
      // Sprint 6 Phase A — send-email dialog state. Sender metadata is part
      // of the operator-safe activity response, not the admin account API.
      sendDialog: {
        open: false,
        contact: null,
        senderEmail: "",
        senderName: "",
      },
      addContactDialog: {
        open: false,
        accountId: null,
        presetEmail: "",
      },
      emailSentTimer: null,
    };
  },
  beforeUnmount() {
    if (this.emailSentTimer) clearTimeout(this.emailSentTimer);
  },
  computed: {
    isAggregate() {
      // We render the "multiple contacts" header when more than one
      // contact comes back, OR when fallback to company was used (even
      // a 1-contact company aggregate is conceptually "an aggregate").
      return Boolean(this.data && (this.data.contacts.length > 1 || this.data.fallback_used));
    },
    canWriteMailjet() {
      return this.data?.permissions?.can_write === true;
    },
    // Entity emails (primary/secondary/tertiary) that are NOT yet linked
    // to a Mailjet contact. Surfaced in the panel so the operator can add
    // them one-click without leaving the page.
    unlinkedEmails() {
      const all = Array.isArray(this.data?.entity_emails) ? this.data.entity_emails : [];
      if (!all.length) return [];
      const linked = new Set(
        (this.data?.contacts || [])
          .map((c) => String(c.email || "").trim().toLowerCase())
          .filter(Boolean)
      );
      return all.filter((email) => {
        const key = String(email || "").trim().toLowerCase();
        return key && !linked.has(key);
      });
    },
  },
  watch: {
    entityId: {
      handler() {
        this.fetchActivity();
      },
      immediate: true,
    },
    entityType() {
      this.fetchActivity();
    },
  },
  methods: {
    extractError(error, fallbackKey) {
      return error?.response?.data?.error || error?.message || this.$t(fallbackKey);
    },
    eventColor(type) {
      return EVENT_COLORS[type] || "default";
    },
    statColor(key) {
      if (key === "open" || key === "click") return "success";
      if (key === "bounce") return "warning";
      if (key === "unsub") return "error";
      return "info";
    },
    relativeTime(value) {
      if (!value) return "—";
      try {
        const diffMs = Date.now() - new Date(value).getTime();
        if (diffMs < 0) return formatDisplayDateTime(value);
        const sec = Math.floor(diffMs / 1000);
        if (sec < 60) return this.$t("ncrm.integrations.mailjet.activity.time.justNow");
        const min = Math.floor(sec / 60);
        if (min < 60) return this.$t("ncrm.integrations.mailjet.activity.time.minutes", { n: min });
        const hr = Math.floor(min / 60);
        if (hr < 24) return this.$t("ncrm.integrations.mailjet.activity.time.hours", { n: hr });
        const day = Math.floor(hr / 24);
        if (day < 30) return this.$t("ncrm.integrations.mailjet.activity.time.days", { n: day });
        return formatDisplayDate(value);
      } catch (_) {
        return String(value);
      }
    },
    goToPerson(personId) {
      if (!personId) return;
      this.$router.push(`/ncrm/people/${personId}`);
    },

    openManageLists(contact) {
      if (!this.canWriteMailjet) return;
      if (!contact?.id || !contact?.account_id) return;
      // Active memberships only (is_active && not unsubscribed) — the dialog
      // pre-ticks these checkboxes.
      const activeListIds = (contact.memberships || [])
        .filter((m) => m.is_active && !m.is_unsubscribed)
        .map((m) => m.list_id);
      this.manageDialog = {
        open: true,
        contact,
        activeListIds,
      };
    },
    onListsSaved() {
      // Refresh the activity panel so the chips reflect the new memberships.
      this.fetchActivity();
    },

    openSendEmail(contact) {
      if (!this.canWriteMailjet) return;
      if (!contact?.id || !contact?.account_id) return;
      this.sendDialog = {
        open: true,
        contact,
        senderEmail: contact.account_default_sender_email || "",
        senderName: contact.account_default_sender_name || "",
      };
    },
    onEmailSent() {
      // Wait a moment so Mailjet's "sent" webhook can land; then refresh.
      // Use 3s — usually plenty for Mailjet to fire the sent event.
      this.emailSentTimer = setTimeout(() => this.fetchActivity(), 3000);
    },

    openAddContact(presetEmail = "") {
      if (!this.canWriteMailjet) return;
      const accounts = Array.isArray(this.data?.available_accounts)
        ? this.data.available_accounts
        : [];
      if (!accounts.length) {
        this.$emit("notify", {
          color: "warning",
          message: this.$t("ncrm.integrations.mailjet.activity.errors.noAccount"),
        });
        return;
      }
      this.addContactDialog = { open: true, accountId: null, presetEmail };
    },
    onContactAdded() {
      // Re-fetch the activity panel so the newly created/linked contact
      // shows up as a card with its memberships + stats.
      this.fetchActivity();
    },

    async fetchActivity() {
      if (!this.entityId) return;
      this.loading = true;
      this.errorMessage = "";
      try {
        const { data } = await axios.get(
          "/ncrm/integrations/mailjet/activity",
          { params: { entityType: this.entityType, entityId: this.entityId } }
        );
        this.data = data;
      } catch (error) {
        this.errorMessage = this.extractError(error, "ncrm.integrations.mailjet.activity.errors.load");
        this.$emit("notify", { color: "error", message: this.errorMessage });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-mailjet-activity__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 8px;
}
.ncrm-mailjet-activity__loader { padding: 12px 0; }

.ncrm-mailjet-activity__identity {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.ncrm-mailjet-activity__section-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--app-text-muted, rgba(0, 0, 0, 0.55));
  margin-bottom: 8px;
}

.ncrm-mailjet-activity__stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
  gap: 8px;
}
.ncrm-mailjet-activity__stat-tile {
  padding: 10px 12px;
  border-radius: 6px;
  background: var(--app-surface-2, rgba(0, 0, 0, 0.04));
  text-align: center;
  border: 1px solid transparent;
}
.ncrm-mailjet-activity__stat-tile--success { border-color: rgba(76, 175, 80, 0.3); }
.ncrm-mailjet-activity__stat-tile--warning { border-color: rgba(255, 152, 0, 0.3); }
.ncrm-mailjet-activity__stat-tile--error   { border-color: rgba(244, 67, 54, 0.3); }
.ncrm-mailjet-activity__stat-tile--info    { border-color: rgba(33, 150, 243, 0.3); }
.ncrm-mailjet-activity__stat-value {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.1;
}
.ncrm-mailjet-activity__stat-label {
  font-size: 11px;
  text-transform: uppercase;
  color: var(--app-text-muted, rgba(0, 0, 0, 0.55));
  letter-spacing: 0.3px;
  margin-top: 2px;
}

.ncrm-mailjet-activity__lists {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ncrm-mailjet-activity__events {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ncrm-mailjet-activity__event-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}
.ncrm-mailjet-activity__event-chip { min-width: 60px; }
.ncrm-mailjet-activity__event-subject {
  flex: 1;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ncrm-mailjet-activity__event-time { flex-shrink: 0; }
</style>
